import React, { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

// Custom plugin to display total on top of bars
const totalOnTopPlugin = {
  id: 'totalOnTop',
  afterDatasetsDraw: function(chart) {
    const ctx = chart.ctx;
    const datasets = chart.data.datasets;
    
    chart.data.labels.forEach((label, index) => {
      let total = 0;
      let topY = 0;
      
      // Calculate total for this bar
      datasets.forEach(dataset => {
        total += dataset.data[index] || 0;
      });
      
      // Find the top position of the stacked bar
      const meta = chart.getDatasetMeta(datasets.length - 1);
      if (meta.data[index]) {
        topY = meta.data[index].y;
      }
      
      // Only display if total > 0
      if (total > 0) {
        ctx.save();
        ctx.font = 'bold 12px Arial';
        ctx.fillStyle = '#333';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(total.toString(), meta.data[index].x, topY - 5);
        ctx.restore();
      }
    });
  }
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  totalOnTopPlugin
);

const RegistrationChart = ({ alumniData }) => {
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: []
  });

  useEffect(() => {
    if (!alumniData || alumniData.length === 0) return;

    // Filter only accepted/in-progress registrations
    const validRegistrations = alumniData.filter(data => 
      ["InProgress", "Accepted"].includes(data.ticket_status?.description)
    );

    // Group registrations by date and year
    const registrationsByDateAndYear = validRegistrations.reduce((acc, registration) => {
      const createdDate = new Date(registration.created_at);
      const dateKey = createdDate.toISOString().split('T')[0]; // YYYY-MM-DD format
      const yearName = registration.passed_out_year?.year_name || 'Unknown';
      
      if (!acc[dateKey]) {
        acc[dateKey] = {};
      }
      
      if (acc[dateKey][yearName]) {
        acc[dateKey][yearName]++;
      } else {
        acc[dateKey][yearName] = 1;
      }
      
      return acc;
    }, {});

    // Get all unique years for consistent coloring
    const allYears = [...new Set(validRegistrations.map(reg => 
      reg.passed_out_year?.year_name || 'Unknown'
    ))].sort();

    // Generate colors for each year - Sequential blue range from light to dark
    const colors = [
      'rgba(224, 247, 250, 0.9)',  // Very light blue
      'rgba(187, 222, 251, 0.8)',  // Light blue
      'rgba(144, 202, 249, 0.8)',  // Medium light blue
      'rgba(100, 181, 246, 0.8)',  // Blue
      'rgba(66, 165, 245, 0.8)',   // Medium blue
      'rgba(42, 148, 243, 0.8)',   // Medium dark blue
      'rgba(30, 136, 229, 0.8)',   // Dark blue
      'rgba(25, 118, 210, 0.8)',   // Darker blue
      'rgba(21, 101, 192, 0.8)',   // Very dark blue
      'rgba(13, 71, 161, 0.9)',    // Darkest blue
    ];

    const borderColors = [
      'rgba(224, 247, 250, 1)',    // Very light blue
      'rgba(187, 222, 251, 1)',    // Light blue
      'rgba(144, 202, 249, 1)',    // Medium light blue
      'rgba(100, 181, 246, 1)',    // Blue
      'rgba(66, 165, 245, 1)',     // Medium blue
      'rgba(42, 148, 243, 1)',     // Medium dark blue
      'rgba(30, 136, 229, 1)',     // Dark blue
      'rgba(25, 118, 210, 1)',     // Darker blue
      'rgba(21, 101, 192, 1)',     // Very dark blue
      'rgba(13, 71, 161, 1)',      // Darkest blue
    ];

    // Get date range from earliest to latest
    const dates = Object.keys(registrationsByDateAndYear).sort();
    if (dates.length === 0) return;

    const startDate = new Date(dates[0]);
    const endDate = new Date(dates[dates.length - 1]);
    
    // Ensure end date is at least today if registrations are still ongoing
    const today = new Date();
    if (endDate < today) {
      endDate.setTime(today.getTime());
    }

    // Generate all dates in range
    const allDates = [];
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dateKey = currentDate.toISOString().split('T')[0];
      allDates.push({
        dateKey,
        label: currentDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        })
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Create datasets for each year
    const datasets = allYears.map((year, index) => ({
      label: year,
      data: allDates.map(date => 
        registrationsByDateAndYear[date.dateKey]?.[year] || 0
      ),
      backgroundColor: colors[index % colors.length],
      borderColor: borderColors[index % borderColors.length],
      borderWidth: 1,
    }));

    setChartData({
      labels: allDates.map(date => date.label),
      datasets: datasets,
    });
  }, [alumniData]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        title: {
          display: true,
          text: 'Date',
          font: {
            size: 14,
            weight: 'bold'
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          font: {
            size: 10
          }
        }
      },
      y: {
        stacked: true,
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Registrations',
          font: {
            size: 14,
            weight: 'bold'
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          stepSize: 1,
          font: {
            size: 12
          }
        }
      },
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: 12,
            weight: 'bold'
          },
          usePointStyle: true,
          padding: 15
        }
      },
      title: {
        display: true,
        text: 'Daily Registration Trends by Graduation Year',
        font: {
          size: 18,
          weight: 'bold'
        },
        padding: {
          top: 10,
          bottom: 30
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        mode: 'index',
        intersect: false,
        callbacks: {
          title: function(context) {
            return `Date: ${context[0].label}`;
          },
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y} registrations`;
          },
          afterBody: function(context) {
            const total = context.reduce((sum, item) => sum + item.parsed.y, 0);
            return [`Total: ${total} registrations`];
          }
        }
      },
      totalOnTop: true
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
    onHover: (event, activeElements) => {
      event.native.target.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
    }
  };

  if (chartData.labels.length === 0) {
    return (
      <div className="chart-container">
        <div style={{ 
          textAlign: 'center', 
          padding: '40px',
          color: '#666',
          fontSize: '16px' 
        }}>
          No registration data available for chart
        </div>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <div style={{ height: '400px', position: 'relative' }}>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};

export default RegistrationChart;